import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, Cancel, Save } from '@mui/icons-material';
import { WriteoffView } from '../../../../../services/WriteoffService/types';
import {
  UPLOAD_DIALOG_STYLES,
  UPLOAD_DIALOG_COLORS,
  ALLOWED_EXTENSIONS,
} from '../../../../common/styles/dialogStyles';
import { useFileUpload } from './hooks';
import { DropZone, FileList, UploadProgress } from './components';

export interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  writeoff: WriteoffView;
}

export const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  open,
  onClose,
  writeoff,
}) => {
  const {
    selectedFiles,
    isUploading,
    uploadProgress,
    error,
    isDragOver,
    fileInputRef,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUpload,
    handleClose,
    handleBrowseFiles,
  } = useFileUpload({ writeoff, onClose });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: UPLOAD_DIALOG_STYLES.paper }}
    >
      <DialogTitle sx={UPLOAD_DIALOG_STYLES.title}>
        <CloudUpload sx={{ color: UPLOAD_DIALOG_COLORS.primary, fontSize: 28 }} />
        Upload Documents
      </DialogTitle>

      <DialogContent sx={UPLOAD_DIALOG_STYLES.content}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: 600, color: UPLOAD_DIALOG_COLORS.textPrimary }}
            >
              Select Documents to Upload
            </Typography>

            <DropZone
              isDragOver={isDragOver}
              onBrowse={handleBrowseFiles}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            />

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept={ALLOWED_EXTENSIONS.join(',')}
            />
          </Box>

          <FileList files={selectedFiles} onRemove={handleRemoveFile} isUploading={isUploading} />

          {isUploading && <UploadProgress progress={uploadProgress} />}

          <Alert severity="info" sx={UPLOAD_DIALOG_STYLES.infoAlert}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Upload guidelines:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • Documents will be stored securely in Azure blob storage
              <br />
              • All uploaded files are associated with this write-off request
              <br />
              • Supported formats: PDF, Word, Excel, Images, and Text files
              <br />• Maximum file size: 10MB per document
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={UPLOAD_DIALOG_STYLES.actions}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={isUploading}
          sx={UPLOAD_DIALOG_STYLES.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <Save />}
          disabled={selectedFiles.length === 0 || isUploading}
          sx={UPLOAD_DIALOG_STYLES.submitButton}
        >
          {isUploading
            ? 'Uploading...'
            : `Upload ${selectedFiles.length} Document${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { useUploadDocumentDialog } from './hooks';
export default UploadDocumentDialog;
